import { useQuery } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router'

import { consulta, pedir } from '../api/nucleo'
import { Tarjeta } from '../componentes/ui'

/**
 * **«Dónde sale»** (CLAUDE.md §9): un formulario creado no aparece en la web hasta que una
 * página lo usa con el bloque «Formulario», y el panel no lo decía —el cliente lo creaba y lo
 * buscaba en su web (auditoría del 29-09-2026)—. Aquí se ven las páginas que lo pintan, con
 * su dirección para probarlo, o cómo ponerlo en una.
 */
export type Donde = {
  estado: null | string
  paginas: Array<{ id: number; titulo: string; publicada: boolean; url: null | string }>
  consultaDeLaTienda: boolean
}

export const useDondeSale = (formulario: number) =>
  useQuery({
    queryKey: ['formulario-donde', formulario],
    queryFn: () => pedir<Donde>(`/formularios/donde${consulta({ id: formulario })}`),
  })

export const ListaDeDondeSale = ({
  formulario,
  espacio,
  sitio,
}: {
  espacio: number
  formulario: number
  sitio: number
}) => {
  const { data, isPending, error } = useDondeSale(formulario)
  const paginas = `/e/${espacio}/s/${sitio}/paginas`
  if (isPending) return <p className="text-sm text-tenue">Mirando…</p>
  if (error || !data)
    return <p className="text-sm text-tenue">No se ha podido mirar en qué páginas sale.</p>
  return (
    <div className="flex flex-col gap-2 text-sm">
      {data.estado && data.estado !== 'activo' && (
        <p className="text-tinta-2">
          Está {data.estado === 'cerrado' ? 'cerrado' : 'en borrador'}: aunque salga en una página,
          no admite respuestas hasta que lo pongas «Activo».
        </p>
      )}
      {data.consultaDeLaTienda && (
        <p>Es el formulario de consulta de la tienda: sale en la ficha de cada producto.</p>
      )}
      {data.paginas.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {data.paginas.map((p) => (
            <li className="flex flex-wrap items-center gap-x-2 gap-y-1" key={p.id}>
              <Link className="font-medium text-violeta underline" to={`${paginas}/${p.id}`}>
                {p.titulo || `Página ${p.id}`}
              </Link>
              {p.url ? (
                <a
                  className="inline-flex items-center gap-1 text-xs text-tenue underline"
                  href={p.url}
                  rel="noreferrer"
                  target="_blank"
                >
                  Probarlo en la web <ExternalLink aria-hidden className="size-3" />
                </a>
              ) : (
                <span className="text-xs text-tenue">
                  {p.publicada ? '' : 'Página sin publicar: no se ve todavía en la web.'}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        !data.consultaDeLaTienda && (
          <p className="text-tinta-2">
            Todavía no sale en ninguna página. Para que se vea en tu web, abre una{' '}
            <Link className="font-medium text-violeta underline" to={paginas}>
              página
            </Link>
            , añade el bloque «Formulario» y elige este. Al publicar la página, se podrá rellenar.
          </p>
        )
      )}
    </div>
  )
}

export const DondeSale = (p: { espacio: number; formulario: number; sitio: number }) => (
  <Tarjeta>
    <p className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">Dónde sale</p>
    <ListaDeDondeSale {...p} />
  </Tarjeta>
)
